import { defineStore } from 'pinia';
import { ref, computed, isRef } from 'vue';
import { useRouter } from 'vue-router';
import { useLocalStorage, StorageSerializers } from '@vueuse/core';
import { useCompanyStore } from '@/modules/company/store';
import { showToast } from '@/utilities/toast-handler';
import { authApi } from '@/utilities/axios';
import { resetUnauthenticatedFlag } from '../../../utilities/axios';
import { UserPermission } from '../../permission/enum';
import axios from 'axios';
import {
        sendSsoTelemetry,
        confirmSsoSession,
        setSsoAttemptId,
        setSsoEmail,
        getSsoEmail,
    } from '@/modules/sso/utils/ssoTelemetry';

const AUTH_URL = process.env.VUE_APP_API_VERSION + '/login';
const VERIFY_OTP_URL = process.env.VUE_APP_API_VERSION + '/verify-otp';
const FORGOT_PASSWORD_URL = process.env.VUE_APP_API_VERSION + '/users/forgot-password';
const PASSWORD_RESET_URL = 'v2/admin/users/password-reset';
const SSO_CALLBACK = process.env.VUE_APP_API_URL + '/auth/callback';


export const useAuthStore = defineStore('auth', () => {
    const router = useRouter();
    const companyStore = useCompanyStore();
    const loggedUser = ref(useLocalStorage('loggedUser', {}));
    const isGodUser = ref(useLocalStorage('isGodUser', false));
    const token = ref(useLocalStorage('token', null));
    const permissionList = ref(useLocalStorage('permissionList', []));
    const productList = ref(useLocalStorage('productList', []));
    const selectedProduct = ref(useLocalStorage('selectedProduct', null));
    const ttl = ref(useLocalStorage('ttl', null));
    const timer = ref(useLocalStorage('timer', null));
    //company logo
    const companyLogo = useLocalStorage('companyLogo', null, { serializer: StorageSerializers.object });
    // states for session timeout
    const lastActive = ref(null);
    const idleTimeout = process.env.VUE_APP_IDLE_CHECK_INTERVAL * 60 * 1000;

    const companyLogoUrl = computed(() => {
        console.log('companyLogo', companyLogo.value , companyLogo);
        // If company logo is fetched → use it
        if (companyLogo.value?.vignette) {
            return companyLogo.value.vignette;
        }
        // Otherwise → fallback to product logo
        if (selectedProduct.value) {
            return require(`@/assets/images/logo/${selectedProduct.value}.png`);
        }
        return null;
    });

    const isFallbackProductLogo = computed(() => {
        return !companyLogo.value?.vignette && selectedProduct.value;
    });

    // getters
    const isLoggedIn = computed(() => {
        return loggedUser.value && token.value ? true : false;
    });

    const loggedUserId = computed(() => {
        return loggedUser.value.id;
    });

    const getLoggedUserCompanyId = computed(() => {
        return loggedUser.value?.company_id;
    });

    const getLoggedUserName = computed(() => {
        return loggedUser.value?.name == 'Root' ? 'System Admin' : `${loggedUser.value?.name} ${loggedUser.value?.surname}`;
    });

    const getProductDetails = computed(() => {
        return selectedProduct.value ? productList.value?.find((product) => product.code === selectedProduct.value) : null;
    });

    const getUserPermissions = computed(() => {
        if (permissionList.value) {
            const permissions = typeof permissionList.value === 'object' ? Object.values(permissionList.value) : permissionList.value;
            return permissions?.map((permission) => permission.code);
        }
        return [];
    });

    // Check if logged user is part of global_task_force
    const userIsGlobalTaskForce = computed(() => {
        return hasPermission(UserPermission.GLOBAL_TASK_FORCE);
    });

    // this function checks if the logged user has the required permission or not and returns a boolean value
    const hasPermission = (requiredPermission) => {
        return requiredPermission ? !!getUserPermissions.value?.includes(requiredPermission) : false;
    };

    // actions
    const login = async (body) => {
        try {
            const response = await authApi.post(AUTH_URL, body);
            if(response.statusCode === 200 && response?.data?.god_user_otp_required ){
                showToast({ severity: 'info', summary: 'OTP Sent' }, 'Enter OTP to continue');
                return { otpGS: true };
            }
            // if OTP is enabled for that user then the status will be 202
            if (response.statusCode === 202) {
                showToast({ severity: 'info', summary: 'OTP Sent' }, 'Enter OTP to continue');
                return { otp: true };
            }
            // if response status is
            else if (response.statusCode === 200) {
                resetUnauthenticatedFlag();
                // after successful login setup user data
                await setupLoggedUser(response.data);
            }
            return response;
        } catch (err) {
            // back validation for email and password errors
            if (err.status === 400) {
                throw err?.data?.data;
            }
            // if email and password is valid but incorrect
            else if (err.status == 401) {
                // toast.add({ severity: 'error', summary: 'Error', detail: 'Unauthorized Credentials', life: 5000 });
                showToast({}, 'Invalid Credentials');
                return {};
            }
        }
    };

    const verifyOtp = async (body) => {
        try {
            const response = await authApi.post(VERIFY_OTP_URL, body);
            if (response.statusCode === 200) {
                resetUnauthenticatedFlag();
                // after successful login setup user data
                await setupLoggedUser(response.data);
            }
        } catch (error) {
            if (error.status == 401) {
                showToast({}, 'Invalid OTP');
                return {};
            }
        }
    };

    /**
     * Auth fields are `ref(useLocalStorage(...))` — nested refs.
     * Assigning outer.value = x replaces the StorageRef and leaves localStorage stale.
     * Always write into the inner StorageRef when present.
     */
    const setPersisted = (holder, value) => {
        if (isRef(holder) && isRef(holder.value)) {
            holder.value.value = value;
            return;
        }
        if (isRef(holder)) {
            holder.value = value;
            return;
        }
        holder = value;
    };

    /**
     * Drop local session leftovers without navigating.
     * Used at SSO start / SSO fail so a stale token cannot conflict with a new Entra bounce.
     */
    const clearLocalSession = () => {
        setPersisted(loggedUser, {});
        setPersisted(isGodUser, false);
        setPersisted(token, null);
        setPersisted(ttl, null);
        setPersisted(timer, null);
        setPersisted(permissionList, []);
        setPersisted(productList, []);
        setPersisted(selectedProduct, null);
        companyLogo.value = null;
        lastActive.value = null;
        try {
            const ac = companyStore.activeCompany;
            if (isRef(ac) && isRef(ac.value)) {
                ac.value.value = {};
            } else if (isRef(ac)) {
                ac.value = {};
            }
        } catch (e) {
            // ignore if company store not ready
        }
    };

    const logout = async () => {
        clearLocalSession();
        await router.push('/');
    };

    const forgotPassword = async (email) => {
        try {
            const response = await authApi.get(FORGOT_PASSWORD_URL, {
                params: { email },
            });
            if (response.statusCode === 200) {
                await showToast(response.toast, 'Password Reset link have been emailed').then(() => {
                    setTimeout(() => {  router.push({ name: 'Login' }); }, 1000);
                });
            }
        } catch (err) {
            // back validation for email and password errors
            if (err.status === 400 || err.status === 401) {
                    showToast({ summary: 'Warning', severity: 'info' }, err?.data?.message || 'If the email address is registered, you will receive password reset instructions shortly');
            }
        }
    };

    const resetPassword = async (body) => {
        return await authApi
            .post(PASSWORD_RESET_URL, body)
            .then(async (res) => {
                let userData = res.data;
                if (res.statusCode == 200 && userData.token) {
                    // after successful login setup user data
                    await setupLoggedUser(userData);
                } else {
                    showToast({}, 'Failed to reset password');
                }
            })
            .catch((error) => {
                showToast({}, error?.data?.message ??  'Failed to reset password');
            });
    };

    const selectProduct = (productCode) => {
        selectedProduct.value = productCode;
        showToast({ severity: 'success', summary: 'Success' }, `${productCode} Selected`);
    };

    const selectCompany = (companyId) => {
        loggedUser.value.company_id = companyId;
        showToast({ severity: 'success', summary: 'Success' }, 'Company Selected');
    };

    // updates active state for user session everytime an event is triggered in the App.vue
    const setLastActive = (time) => {
        lastActive.value = time;
    };
    // helper function
    // setups user details
    // if user doesnt have a valid license then log off the user accordingly
    function setupLoggedUser(data) {
        // Reset every login: a stale localStorage value from a previous god
        // session would otherwise leak god-only menu items into the new user.
        setPersisted(isGodUser, !data.user.company_id);
        setPersisted(loggedUser, data.user);
        setPersisted(token, data.token);
        setPersisted(ttl, data.ttl);
        setPersisted(permissionList, data.permissionList);
        setPersisted(productList, data.productList);
        companyLogo.value = data.company?.logo ?? null;

        if (data.sso_attempt_id) {
            setSsoAttemptId(data.sso_attempt_id);
        }

        // check if user have enough products i.e valid licenses, if not logout that user
        if (productList.value?.length === 0) {
            // NWFC-style: Authenticating → login bounce after backend SSO success
            sendSsoTelemetry('fe_no_license', {
                email: data.user?.email || getSsoEmail(),
                failure_reason: 'Backend SSO succeeded but frontend logged user out: no valid product licenses',
                user_id: data.user?.id ?? null,
                company_id: data.user?.company_id ?? null,
                product_count: 0,
                detail: 'setupLoggedUser productList.length === 0',
            });
            showToast({ severity: 'info', summary: 'Invalid License' }, 'No valid licenses found for user');
            setTimeout(() => {
                logout();
            }, 2000);
        } else {
            // trigger token refresh timer
            setTokenRefreshTimer();
            // trigger session timeout timer
            startIdleTimeoutChecker();
            // send to an intermediate page of product selection
            router.push({ name: 'ProductSelect' });
            // Telemetry first (needs attempt id), then confirm (clears attempt id)
            (async () => {
                await sendSsoTelemetry('fe_navigated_product_select', {
                    email: data.user?.email || getSsoEmail(),
                    detail: 'Navigated to ProductSelect after SSO',
                    user_id: data.user?.id ?? null,
                    company_id: data.user?.company_id ?? null,
                    product_count: Array.isArray(productList.value) ? productList.value.length : null,
                });
                await confirmSsoSession(data.token);
            })();
        }
    }
    function setTokenRefreshTimer() {
        timer.value = setTimeout(() => {
            // refresh the token with updated token
            // call refreshToken function here
        }, (ttl.value - 1) * 60 * 1000); //multiply by 60 * 1000  (seconds * milliseconds)
    }
    function cleanCache() {
        companyStore.cleanCache();
        window.location.reload();
    }
    // for idle timer check

    function startIdleTimeoutChecker() {
        let intervalId = setInterval(() => {
            let timeDiff = lastActive.value && Date.now() - lastActive.value;
            let timeOut = idleTimeout;
            if (timeDiff > timeOut) {
                logout();
                clearInterval(intervalId);
                // redirect to login if session timeout
                router.push({ name: 'Login' });
            }
        }, idleTimeout); //checks every 10 minutes
    }

   const callbackSSO = async(authCode, state, session_state)  =>{
        const emailHint = getSsoEmail();
        try {
            const params = new URLSearchParams();
            params.set('code', authCode);
            params.set('state', state);
            if (session_state) {
                params.set('session_state', session_state);
            }
            const callbcakUrl = `${SSO_CALLBACK}?${params.toString()}`;
            const response = await axios.get(callbcakUrl);
            const payload = response.data;
            if (payload?.data?.sso_attempt_id) {
                setSsoAttemptId(payload.data.sso_attempt_id);
            } else if (payload?.sso_attempt_id) {
                setSsoAttemptId(payload.sso_attempt_id);
            }

            const userEmail = payload?.data?.user?.email || emailHint;
            if (userEmail) {
                setSsoEmail(userEmail);
            }

            const products = payload?.data?.productList;
            const productCount = Array.isArray(products)
                ? products.length
                : (products && typeof products === 'object' ? Object.keys(products).length : null);
            const hasToken = !!payload?.data?.token;
            const ok = response.status === 200 && payload?.status === 'OK' && hasToken;

            const microsoftFromApi = payload?.microsoft_error_description || payload?.microsoft_error || payload?.message || null;

            // Report what FE actually received from /api/auth/callback (no token value)
            await sendSsoTelemetry('fe_callback_response', {
                email: userEmail,
                success: ok,
                has_token: hasToken,
                http_status: response.status,
                backend_status: payload?.microsoft_error || payload?.status || null,
                backend_message: microsoftFromApi,
                user_id: payload?.data?.user?.id ?? null,
                company_id: payload?.data?.user?.company_id ?? null,
                product_count: productCount,
                failure_reason: ok
                    ? null
                    : (microsoftFromApi || `Callback response not usable (status=${payload?.status || response.status}, has_token=${hasToken})`),
            });

            if (ok) {
                resetUnauthenticatedFlag();
                await setupLoggedUser(payload.data);
                return { success: true, email: userEmail };
            }

            return {
                success: false,
                email: userEmail,
                failure_reason: microsoftFromApi
                    || `AMS callback did not return a usable session (status=${payload?.status || response.status})`,
                http_status: response.status,
                backend_status: payload?.microsoft_error || payload?.status || null,
                backend_message: microsoftFromApi,
                user_id: payload?.data?.user?.id ?? null,
                company_id: payload?.data?.user?.company_id ?? null,
                product_count: productCount,
                detail: microsoftFromApi || 'SSO callback failed',
            };
        } catch (error) {
            const errData = error?.response?.data;
            const microsoftFromApi = errData?.microsoft_error_description || errData?.message || error?.message || null;
            await sendSsoTelemetry('fe_callback_response', {
                email: emailHint,
                success: false,
                has_token: false,
                http_status: error?.response?.status ?? null,
                backend_status: errData?.microsoft_error || errData?.status || null,
                backend_message: microsoftFromApi,
                failure_reason: microsoftFromApi || 'Frontend request to AMS /auth/callback failed',
                error_name: error?.name || 'Error',
            });

            return {
                success: false,
                email: emailHint,
                failure_reason: microsoftFromApi || 'Frontend request to AMS /auth/callback failed',
                http_status: error?.response?.status ?? null,
                backend_status: errData?.microsoft_error || errData?.status || null,
                backend_message: microsoftFromApi,
                error_name: error?.name || 'Error',
                detail: microsoftFromApi || 'callbackSSO exception',
            };
        }
    }

    return {
        // states
        loggedUser,
        isGodUser,
        token,
        ttl,
        timer,
        permissionList,
        productList,
        selectedProduct,
        // getters
        isLoggedIn,
        loggedUserId,
        getLoggedUserCompanyId,
        getLoggedUserName,
        getProductDetails,
        getUserPermissions,
        userIsGlobalTaskForce,
        // actions
        hasPermission,
        login,
        logout,
        clearLocalSession,
        callbackSSO,
        forgotPassword,
        resetPassword,
        selectProduct,
        selectCompany,
        setLastActive,
        // helpers
        cleanCache,
        verifyOtp,
        companyLogo,
        setupLoggedUser,
        companyLogoUrl,
        isFallbackProductLogo
    };
});
